import api from './client'

export interface AnalyticsSessionResponse {
  sessionId: string
}

export type AnalyticsEventType = 'PAGEVIEW' | 'FEATURE_CLICK' | 'API_CALL' | 'ERROR' | 'CONVERSION'

function getOrCreateAnonymousId(): string {
  const key = 'edive-anon-id'
  let id = localStorage.getItem(key)
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem(key, id)
  }
  // Mirror nel cookie 'edive_vid' (leggibile lato server) per l'attribuzione marketing
  try {
    if (typeof document !== 'undefined' && !document.cookie.includes('edive_vid=')) {
      document.cookie = `edive_vid=${id}; path=/; max-age=31536000; SameSite=Lax`
    }
  } catch { /* no-op */ }
  return id
}

function getUtmParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search)
  const result: Record<string, string> = {}
  for (const [k, field] of [
    ['utm_source', 'utmSource'],
    ['utm_medium', 'utmMedium'],
    ['utm_campaign', 'utmCampaign'],
    ['utm_term', 'utmTerm'],
    ['utm_content', 'utmContent'],
  ] as [string, string][]) {
    const v = params.get(k)
    if (v) result[field] = v
  }
  return result
}

export const analyticsApi = {
  getAnonymousId: getOrCreateAnonymousId,

  createSession: () =>
    api
      .post<AnalyticsSessionResponse>('/v1/analytics/sessions', {
        anonymousId: getOrCreateAnonymousId(),
        referrer: document.referrer || undefined,
        ...getUtmParams(),
      }, { _skipToast: true } as any)
      .then((r) => r.data),

  trackEvent: (data: {
    sessionId: string
    eventType?: AnalyticsEventType
    eventName: string
    pagePath?: string
    payload?: Record<string, unknown>
  }) =>
    api
      .post('/v1/analytics/events', {
        sessionId: data.sessionId,
        events: [
          {
            eventType: data.eventType ?? 'PAGEVIEW',
            eventName: data.eventName,
            pagePath: data.pagePath,
            payload: data.payload,
          },
        ],
      }, { _skipToast: true } as any)
      .then((r) => r.data),

  // Admin: dashboard stats
  dashboard: (params?: { from?: string; to?: string }) =>
    api
      .get<{
        totalSessions: number
        uniqueUsers: number
        totalPageviews: number
        newUsers: number
        returningUsers: number
        sessionsTrend: number
        usersTrend: number
        topPages: { path: string; count: number }[]
        topEvents: { name: string; count: number }[]
        dailyActiveUsers: { date: string; count: number }[]
        deviceBreakdown: { device: string; count: number }[]
        topReferrers: { domain: string; count: number }[]
        utmSources: { source: string; count: number }[]
        hourlyDistribution: { hour: number; count: number }[]
      }>('/v1/analytics/dashboard', { params })
      .then((r) => r.data),

  // Admin: funnel analysis
  funnel: (name: string, params?: { from?: string; to?: string }) =>
    api
      .get<{
        funnelName: string
        title: string
        from: string
        to: string
        steps: { step: number; label: string; users: number; dropoffPct: number }[]
        overallConversionPct: number
      }>(`/v1/analytics/funnel/${name}`, { params })
      .then((r) => r.data),

  // Admin: per-user event timeline
  userTimeline: (userId: string, params?: { limit?: number; offset?: number }) =>
    api
      .get<{
        userId: string
        events: { id: string; eventType: string; eventName: string; pagePath: string | null; createdAt: string }[]
        total: number
      }>(`/v1/analytics/users/${userId}/timeline`, { params })
      .then((r) => r.data),

  // Admin: attribuzione iscrizioni → sorgente/campagna (loop marketing chiuso)
  attributionSummary: () =>
    api
      .get<{
        totalAttributed: number
        byCampaign: { source: string; medium: string; campaign: string; signups: number }[]
      }>('/v1/analytics/attribution/summary')
      .then((r) => r.data),
}
